import React, { lazy, useEffect, useState, type ComponentType, type FC } from 'react';
import { useRouter } from 'next/router';
import { useSDMCartContext, useSDMCustomerContext, useSDMViewportContext } from '@ldp/sdm-contexts';

import { IErrorDisplay } from './error-display.interface';
import styles from './error-display.module.scss';

import Footer from '@/components/footer';
import Masthead from '@/components/masthead';
import { Spinner } from '@/components/shared';
import { alertOctagonIcon, VIEW_RESOLVER, LoggedInStatus } from '@/data/constants';
import { getErrorMessage, getSelectedProvinceCookie } from '@/utils';
import { useCommonContextProps } from '@/hooks';
import { getAuthorizationHeader, getExistingCartGuid, getLoggedInStatus, getSiteId } from 'Utils/auth';
import Image from 'next/image';
import { useIntl } from 'react-intl';

const ErrorDisplay: FC<IErrorDisplay> = ({ error = {} }) => {
  const { b3, status } = error;
  const router = useRouter();
  const { formatMessage } = useIntl();
  const { TENANT_ID_SDM, APP_TYPE_WEB, API_VERSION_V2, PAGE_ID_HEADER_FOOTER } = VIEW_RESOLVER;
  const { commonContextProps, locale } = useCommonContextProps();
  const [ViewResolverSDM, setViewResolverSDM] = useState<ComponentType<any> | null>(null);
  const { titleId, subtitleId } = getErrorMessage(status);
  const sdmCartProviderProps = useSDMCartContext();
  const sdmCustomerProviderProps = useSDMCustomerContext();
  const sdmViewportProviderProps = useSDMViewportContext();

  const preferredProvince = getSelectedProvinceCookie();
  const cartId = getLoggedInStatus() === LoggedInStatus.CURRENT ? '' : getExistingCartGuid();
  const headerSection = {};

  useEffect(() => {
    if (typeof window !== 'undefined') {
      // @ts-ignore
      setViewResolverSDM(lazy(() => import('ldpViewResolverSdm/Resolver')));
    }
  }, []);

  if (!ViewResolverSDM) {
    return <Spinner />;
  }

  return (
    <ViewResolverSDM
      pageId={PAGE_ID_HEADER_FOOTER}
      fetchOptions={{
        method: 'POST',
        body: JSON.stringify({
          banner: getSiteId(),
          cartId: cartId,
          ...(preferredProvince ? { province: preferredProvince } : {}),
        }),
        headers: {
          'x-loblaw-tenant-id': TENANT_ID_SDM,
          'x-application-type': APP_TYPE_WEB,
          'Accept-Language': locale,
          Authorization: getAuthorizationHeader(),
          'Content-Type': 'application/json',
        },
      }}
      hadExistingSentry={true}
      isPreview={false}
      previewDatetime={'2023-06-16T15:50-04:00'}
      commonContexts={commonContextProps}
      sdmContexts={{
        sdmCartProviderProps,
        sdmCustomerProviderProps,
        sdmRouterProps: { router },
        sdmViewportProviderProps,
      }}
      PageLoadingFallback={() => <Spinner />}
      locale={locale}
      env={process.env.NEXT_PUBLIC_VIEW_RESOLVER_ENV}
      apiVersion={API_VERSION_V2}
    >
      <Masthead headerSection={headerSection} />
      <div id="page-not-found" className={styles.pageNotFoundContainer}>
        <Image src={alertOctagonIcon} alt="alert" width={35} height={35} />
        <h1>{formatMessage({ id: titleId })}</h1>
        <h2>{formatMessage({ id: subtitleId })}</h2>
        <button onClick={() => router.push('/')}>{formatMessage({ id: 'goToHomePage' })}</button>
      </div>
      <p className={styles.traceId}>traceID: {b3}</p>
      <Footer />
    </ViewResolverSDM>
  );
};

export default ErrorDisplay;
